import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@material-ui/core';

import Modal from 'features/app/components/Modal';
import { CreatePartnerUser } from 'features/users/ts/CreatePartnerUser';
import { useTranslation } from 'hooks/useTranslation';
import { InputField } from 'shared/forms';
import useValidation from 'hooks/useValidation';
import { SelectInputField } from 'features/app/components/SelectInputField';
import { useStyles } from './styles';
import BaseButton from 'features/app/components/BaseButton';
import { fetchRoleItems } from 'features/users/utils/fetchRoleItems';
import { useMessage } from 'features/message/hooks/useMessage';
import { ErrorUtils } from 'utils';
import { createUserRequest } from 'features/users/api';
import { mapSelectOutput } from 'features/app/utils/mapSelectOutput';
import { parseFieldErrors } from 'utils/error-utils';
import { confirmDialog } from 'features/app/utils/confirmDialog';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => Promise<unknown>;
}

const DEFAULT_VALUES: Partial<CreatePartnerUser> = {
  email: '',
  first_name: '',
  last_name: '',
  roles: [],
};

export const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, onSubmit: onModalSubmit, open }) => {
  const { t } = useTranslation('users');
  const { required, email } = useValidation();
  const classes = useStyles();
  const { showErrorMessage, showSuccessMessage } = useMessage();

  const form = useForm<CreatePartnerUser>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    setError,
  } = form;

  const onSubmit = async (values: CreatePartnerUser) => {
    try {
      await createUserRequest(mapSelectOutput(['roles'], values));
      await onModalSubmit?.();
      showSuccessMessage(t('userMessages.userAdded'));
      onClose();
    } catch (err) {
      const parsedError = ErrorUtils.convertError(err);

      parseFieldErrors<CreatePartnerUser>(parsedError).forEach(({ field, ...error }) => setError(field, error));

      showErrorMessage(parsedError);
    }
  };

  const handleClose = React.useCallback(async () => {
    let shouldClose: undefined | boolean = true;

    if (isDirty) {
      shouldClose = await confirmDialog({ title: t('formLeavingPrompt', { ns: 'common' }) });
    }

    if (shouldClose) {
      onClose();
    }
  }, [onClose, t, isDirty]);

  return (
    <Modal open={open} onClose={handleClose} title={t('addUser.title')} paperClass={classes.container}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <Box display="flex" justifyContent="space-between">
            <Box flex={0.48}>
              <InputField
                name="first_name"
                fullWidth
                placeholder={t('inputs.enter', { ns: 'common' })}
                label={t('editProfile.firstName')}
                required
                type="text"
                rules={{
                  validate: {
                    ...required,
                  },
                }}
              />
            </Box>
            <Box flex={0.48}>
              <InputField
                name="last_name"
                fullWidth
                placeholder={t('inputs.enter', { ns: 'common' })}
                label={t('editProfile.lastName')}
                required
                type="text"
                rules={{
                  validate: {
                    ...required,
                  },
                }}
              />
            </Box>
          </Box>
          <Box marginTop={6}>
            <InputField
              name="email"
              fullWidth
              placeholder={t('inputs.emailPlaceholder', { ns: 'common' })}
              label={t('email')}
              required
              type="email"
              rules={{
                validate: {
                  ...required,
                  ...email,
                },
              }}
            />
          </Box>
          <Box marginTop={6}>
            <SelectInputField
              name="roles"
              label={t('addUser.role')}
              isAsync
              fetchItems={fetchRoleItems}
              /**
               * Made as multiselect for making system extendable to future changing of role system.
               */
              isMulti
              maxLength={1}
              rules={{
                validate: {
                  ...required,
                },
              }}
            />
          </Box>
          <Box className={classes.buttonsContainer}>
            <Box width="35%" display="flex">
              <BaseButton variant="text" className={classes.cancelButton} onClick={onClose}>
                {t('cancel', { ns: 'common' })}
              </BaseButton>
              <BaseButton className={classes.saveButton} type="submit" isLoading={isSubmitting}>
                {t('addUser.submitButton')}
              </BaseButton>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Modal>
  );
};
